// ADR-017 Fase 4: SQLite local eliminado — leer de remote/cache.
import { getCatalogRamSnapshotCache } from '../../catalog/infra/catalogRamSnapshotCache'
import type { WebCatalogPriceListRow } from '../../catalog/infra/catalogRamSnapshotCache';
import type { PriceList, PriceListItem, PriceListWithItems } from '../domain/priceListTypes';
type PriceListRow = {
  id: string;
  organization_id: string;
  name: string;
  code: string | null;
  is_default: number;
  applies_online: number;
  is_active: number;
  pricing_mode: string;
  created_at: string | null;
  updated_at: string | null;
  deleted_at: string | null;
};

type PriceListItemRow = {
  id: string;
  price_list_id: string;
  product_id: string;
  variant_id: string | null;
  price: number | null;
  percent_off: number | null;
  min_qty: number | null;
  created_at: string | null;
  updated_at: string | null;
};

function mapList(row: PriceListRow): PriceList {
  return {
    id: row.id,
    organizationId: row.organization_id,
    name: row.name,
    code: row.code,
    isDefault: row.is_default === 1,
    appliesOnline: row.applies_online === 1,
    isActive: row.is_active === 1,
    pricingMode: row.pricing_mode === 'percent_off_base' ? 'percent_off_base' : 'fixed',
    createdAt: row.created_at,
    updatedAt: row.updated_at,
    deletedAt: row.deleted_at,
  };
}

function mapItem(row: PriceListItemRow): PriceListItem {
  return {
    id: row.id,
    priceListId: row.price_list_id,
    productId: row.product_id,
    variantId: row.variant_id,
    price: row.price,
    percentOff: row.percent_off,
    minQty: row.min_qty,
    createdAt: row.created_at,
    updatedAt: row.updated_at,
  };
}

function mapWebPriceListRow(row: WebCatalogPriceListRow): PriceListRow {
  return {
    id: String(row.id),
    organization_id: String(row.organization_id ?? ''),
    name: String(row.name ?? ''),
    code: row.code != null ? String(row.code) : null,
    is_default: row.is_default === true ? 1 : 0,
    applies_online: (row as { applies_online?: boolean | number }).applies_online === true || (row as { applies_online?: boolean | number }).applies_online === 1 ? 1 : 0,
    is_active: row.is_active !== false ? 1 : 0,
    pricing_mode: String(row.pricing_mode ?? 'fixed'),
    created_at: row.created_at != null ? String(row.created_at) : null,
    updated_at: row.updated_at != null ? String(row.updated_at) : null,
    deleted_at: row.deleted_at != null ? String(row.deleted_at) : null,
  };
}

function listPriceListsFromWebCache(): PriceList[] | null {
  const cached = getCatalogRamSnapshotCache();
  if (!cached) return [];
  return cached.priceLists
    .filter((row) => !row.deleted_at)
    .map((row) => mapList(mapWebPriceListRow(row)))
    .sort((a, b) => {
      if (a.isDefault !== b.isDefault) return a.isDefault ? -1 : 1;
      return a.name.localeCompare(b.name);
    });
}

function getPriceListItemsFromWebCache(priceListId: string): PriceListItem[] {
  const cached = getCatalogRamSnapshotCache();
  if (!cached) return [];
  return cached.priceListItems
    .filter((item) => String(item.price_list_id ?? '') === priceListId)
    .map((item) =>
      mapItem({
        id: String(item.id),
        price_list_id: String(item.price_list_id ?? ''),
        product_id: String(item.product_id ?? ''),
        variant_id: item.variant_id != null ? String(item.variant_id) : null,
        price: item.price != null ? Number(item.price) : null,
        percent_off: item.percent_off != null ? Number(item.percent_off) : null,
        min_qty: item.min_qty != null ? Number(item.min_qty) : null,
        created_at: item.created_at != null ? String(item.created_at) : null,
        updated_at: item.updated_at != null ? String(item.updated_at) : null,
      }),
    )
    .sort((a, b) => a.productId.localeCompare(b.productId));
}

export async function listPriceListsLocal(): Promise<PriceList[]> {
  // ADR-017 Fase 4: SQLite local eliminado — leer de remote/cache.
  return listPriceListsFromWebCache() ?? [];
}

export async function listActivePriceListsLocal(): Promise<PriceList[]> {
  // ADR-017 Fase 4: SQLite local eliminado — leer de remote/cache.
  return (listPriceListsFromWebCache() ?? []).filter((row) => row.isActive);
}

export async function getPriceListByIdLocal(id: string): Promise<PriceListWithItems | null> {
  const lists = listPriceListsFromWebCache() ?? [];
        const list = lists.find((row) => row.id === id);
        if (!list) return null;
        return { ...list, items: getPriceListItemsFromWebCache(id) };
}

export async function getPriceListItemsLocal(priceListId: string): Promise<PriceListItem[]> {
  return getPriceListItemsFromWebCache(priceListId);
}

/** Cantidad de ítems por lista, desde el snapshot RAM (sin IO extra). */
export async function listPriceListItemCountsLocal(): Promise<Map<string, number>> {
  const cached = getCatalogRamSnapshotCache();
  const counts = new Map<string, number>();
  if (!cached) return counts;
  for (const item of cached.priceListItems) {
    const listId = String(item.price_list_id ?? '');
    if (!listId) continue;
    counts.set(listId, (counts.get(listId) ?? 0) + 1);
  }
  return counts;
}

export async function getDefaultPriceListLocal(): Promise<PriceList | null> {
  const lists = listPriceListsFromWebCache() ?? [];
        return lists.find((row) => row.isActive && row.isDefault) ?? null;
}
